import { useState } from 'react';

export type AvatarSize   = 'xs' | 'sm' | 'md' | 'lg';
export type AvatarStatus = 'online' | 'offline' | 'busy';

export interface AvatarProps {
  name: string;
  src?: string;
  size?: AvatarSize;
  status?: AvatarStatus;
  title?: string;
  onClick?: () => void;
}

// ── Sizing ────────────────────────────────────────────────────────────────────

const SZ:  Record<AvatarSize, number> = { xs: 20, sm: 28, md: 36, lg: 48 };
const FS:  Record<AvatarSize, string> = { xs: '9px', sm: '11px', md: '13px', lg: '17px' };
const DOT: Record<AvatarSize, number> = { xs: 6, sm: 8, md: 10, lg: 12 };

// ── Deterministic hash colour ─────────────────────────────────────────────────
// 6-bucket palette using existing tokens — same name always yields same colour.

const PALETTE: Array<{ bg: string; fg: string }> = [
  { bg: 'var(--evo-cyan)',     fg: 'var(--evo-fg-on-cyan)' },
  { bg: 'var(--evo-amber)',    fg: 'var(--evo-bg)' },
  { bg: 'var(--evo-blue-500)', fg: '#fff' },
  { bg: 'var(--evo-green-600)', fg: '#fff' },
  { bg: 'var(--evo-slate-200)', fg: 'var(--evo-bg)' },
  { bg: 'var(--evo-steel)',    fg: '#fff' },
];

function hashName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % PALETTE.length;
}

function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const first = parts[0] ?? '';
  if (parts.length === 1) {
    return first.slice(0, 2).toUpperCase();
  }
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

// ── Status dot colour ─────────────────────────────────────────────────────────

const STATUS_BG: Record<AvatarStatus, string> = {
  online:  'var(--evo-success)',
  offline: 'var(--evo-fg-4)',
  busy:    'var(--evo-danger)',
};

// ── Avatar ────────────────────────────────────────────────────────────────────

export function Avatar({
  name,
  src,
  size = 'md',
  status,
  title,
  onClick,
}: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);

  const sz = SZ[size];
  const showImg = src != null && src !== '' && !imgFailed;
  const palette = PALETTE[hashName(name)] ?? PALETTE[0]!;

  return (
    <span
      title={title ?? name}
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: sz,
        height: sz,
        flexShrink: 0,
        borderRadius: '50%',
        userSelect: 'none',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {/* inner circle — clips image to circle */}
      <span
        aria-hidden={false}
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: showImg ? 'var(--evo-bg-3)' : palette.bg,
          color: palette.fg,
          fontSize: FS[size],
          fontWeight: 500,
          fontFamily: 'var(--evo-font)',
          lineHeight: 1,
          letterSpacing: 0,
          border: '1px solid var(--evo-stroke)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        {showImg ? (
          <img
            src={src}
            alt={name}
            onError={() => setImgFailed(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span aria-hidden>{initials(name)}</span>
        )}
      </span>

      {status != null && (
        <span
          aria-label={status}
          style={{
            position: 'absolute',
            right: -1,
            bottom: -1,
            width: DOT[size],
            height: DOT[size],
            borderRadius: '50%',
            background: STATUS_BG[status],
            border: '1.5px solid var(--evo-bg)',
            boxSizing: 'border-box',
            zIndex: 1,
          }}
        />
      )}
    </span>
  );
}
